import React, { useContext, useEffect, useState } from 'react';
import { Bounce, toast } from 'react-toastify';
import { BASE_URL_API } from '../../References/URLs';
import AuthContext from '../../store/auth-context';
import styles from './ExpenseSetting.module.css';

const ExpenseSetting = () => {
    const authCtx = useContext(AuthContext);

    // --- State ---
    const [activeTab, setActiveTab] = useState('categories');
    const [standardCategoryData, setStandardCategoryData] = useState([]);
    const [customCategories, setCustomCategories] = useState([]);

    const [newCategory, setNewCategory] = useState('');
    const [newCategoryDesc, setNewCategoryDesc] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [addLoading, setAddLoading] = useState(false);

    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [editLoading, setEditLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterActive, setFilterActive] = useState('all'); // all | active | inactive

    // --- API Calls ---
    const fetchStandardExpensesCategoryData = async () => {
        try {
            const response = await fetch(BASE_URL_API + "fetchStandardExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                setStandardCategoryData(respo.response);
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            console.error("fetchStandardExpensesCategoryData error", e);
        }
    };

    const fetchExpensesCategoryData = async () => {
        try {
            const response = await fetch(BASE_URL_API + "fetchExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                setCustomCategories(respo.response);
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            console.error("fetchExpensesCategoryData error", e);
        }
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategory.trim()) {
            toast.error('Category name is required', {
                position: "top-center", autoClose: 3000,
                hideProgressBar: true, theme: "light", transition: Bounce,
            });
            return;
        }
        setAddLoading(true);
        try {
            const response = await fetch(BASE_URL_API + "addExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    categoryName: newCategory.trim(),
                    categoryDescription: newCategoryDesc.trim()
                })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                toast.success('Category added', { position: "top-center", autoClose: 2000, hideProgressBar: true });
                setNewCategory('');
                setNewCategoryDesc('');
                setIsAdding(false);
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                toast.error(respo.message || 'Failed to add category', { position: "top-center", autoClose: 3000, hideProgressBar: true });
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
        setAddLoading(false);
    };

    const handleToggleActive = async (cat) => {
        try {
            const response = await fetch(BASE_URL_API + "toggleExpensesCategoryStatus", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    categoryId: cat.categoryId,
                    isActive: !cat.isActive
                })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            console.error("toggleExpensesCategoryStatus error", e);
        }
    };

    const handleEditSave = async (cat) => {
        if (!editName.trim()) {
            toast.error('Category name is required', { position: "top-center", autoClose: 3000, hideProgressBar: true });
            return;
        }
        setEditLoading(true);
        try {
            const response = await fetch(BASE_URL_API + "updateExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    categoryId: cat.categoryId,
                    categoryName: editName.trim(),
                    categoryDescription: editDesc.trim()
                })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                toast.success('Category updated', { position: "top-center", autoClose: 2000, hideProgressBar: true });
                setEditId(null);
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
        setEditLoading(false);
    };

    const handleDeleteCategory = async (cat) => {
        if (!window.confirm(`Delete "${cat.categoryName}"? This cannot be undone.`)) return;
        try {
            const response = await fetch(BASE_URL_API + "deleteExpensesCategoryData", {
                method: "POST",
                body: JSON.stringify({ token: authCtx.token, categoryId: cat.categoryId })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                toast.success('Category deleted', { position: "top-center", autoClose: 2000, hideProgressBar: true });
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
    };

    const handleImportStandard = async (cat) => {
        try {
            const response = await fetch(BASE_URL_API + "importStandardExpensesCategory", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    categoryName: cat.categoryName,
                    categoryDescription: cat.categoryDescription
                })
            });
            const respo = await response.json();
            if (respo.status === "success") {
                toast.success(`"${cat.categoryName}" imported`, { position: "top-center", autoClose: 2000, hideProgressBar: true });
                fetchExpensesCategoryData();
            } else if (respo.message === "tokenExpired") {
                authCtx.logout();
            }
        } catch (e) {
            toast.error('Server error', { position: "top-center", autoClose: 3000, hideProgressBar: true });
        }
    };

    // --- Derived ---
    const filteredCustom = customCategories.filter(cat => {
        const matchSearch = cat.categoryName?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchActive =
            filterActive === 'all' ? true :
            filterActive === 'active' ? cat.isActive :
            !cat.isActive;
        return matchSearch && matchActive;
    });

    const importedNames = customCategories.map(c => c.categoryName?.toLowerCase());
    const notImportedStandard = standardCategoryData.filter(
        s => !importedNames.includes(s.categoryName?.toLowerCase())
    );

    useEffect(() => {
        fetchStandardExpensesCategoryData();
        fetchExpensesCategoryData();
    }, []);

    // --- Tabs config (future tabs plug in here) ---
    const tabs = [
        { key: 'categories', label: 'Categories' },
        { key: 'paymentMethods', label: 'Payment Methods', disabled: true },
        { key: 'budgets', label: 'Budgets', disabled: true },
        { key: 'gst', label: 'GST / Tax', disabled: true },
    ];

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.mainContainer}>

                {/* Page Header */}
                <div className={styles.pageHeader}>
                    <div className={styles.pageHeaderLeft}>
                        <span className={styles.pageTitle}>Expense Settings</span>
                        <span className={styles.pageCrumb}>Settings &rsaquo; Expenses</span>
                    </div>
                </div>

                {/* Tabs */}
                <div className={styles.tabBar}>
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            className={`${styles.tabBtn} ${activeTab === tab.key ? styles.tabBtnActive : ''} ${tab.disabled ? styles.tabBtnDisabled : ''}`}
                            onClick={() => !tab.disabled && setActiveTab(tab.key)}
                        >
                            {tab.label}
                            {tab.disabled && <span className={styles.soonBadge}>soon</span>}
                        </button>
                    ))}
                </div>

                {/* Categories Tab */}
                {activeTab === 'categories' && (
                    <div className={styles.tabContent}>

                        {/* Two-column layout */}
                        <div className={styles.twoCol}>

                            {/* LEFT: Custom Categories */}
                            <div className={styles.panel}>
                                <div className={styles.panelHeader}>
                                    <div className={styles.panelTitleGroup}>
                                        <span className={styles.panelTitle}>Your Categories</span>
                                        <span className={styles.panelCount}>{customCategories.length}</span>
                                    </div>
                                    <button
                                        className={styles.addBtn}
                                        onClick={() => { setIsAdding(true); setNewCategory(''); setNewCategoryDesc(''); }}
                                    >
                                        + Add
                                    </button>
                                </div>

                                {/* Search + Filter */}
                                <div className={styles.filterRow}>
                                    <input
                                        type="text"
                                        placeholder="Search categories..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className={styles.searchInput}
                                    />
                                    <div className={styles.filterPills}>
                                        {['all', 'active', 'inactive'].map(f => (
                                            <button
                                                key={f}
                                                onClick={() => setFilterActive(f)}
                                                className={`${styles.filterPill} ${filterActive === f ? styles.filterPillActive : ''}`}
                                            >
                                                {f.charAt(0).toUpperCase() + f.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Inline Add Form */}
                                {isAdding && (
                                    <form onSubmit={handleAddCategory} className={styles.inlineForm}>
                                        <input
                                            type="text"
                                            placeholder="Category name *"
                                            value={newCategory}
                                            onChange={e => setNewCategory(e.target.value)}
                                            className={styles.inlineInput}
                                            autoFocus
                                        />
                                        <input
                                            type="text"
                                            placeholder="Description (optional)"
                                            value={newCategoryDesc}
                                            onChange={e => setNewCategoryDesc(e.target.value)}
                                            className={styles.inlineInput}
                                        />
                                        <div className={styles.inlineFormActions}>
                                            <button type="submit" className={styles.saveBtn} disabled={addLoading}>
                                                {addLoading ? 'Saving...' : 'Save'}
                                            </button>
                                            <button type="button" className={styles.cancelBtn} onClick={() => setIsAdding(false)}>
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {/* Category List */}
                                <div className={styles.categoryList}>
                                    {filteredCustom.length === 0 && (
                                        <div className={styles.emptyState}>
                                            {searchQuery ? 'No categories match your search.' : 'No categories yet. Add one or import from standards.'}
                                        </div>
                                    )}
                                    {filteredCustom.map(cat => (
                                        <div
                                            key={cat.categoryId}
                                            className={`${styles.categoryRow} ${!cat.isActive ? styles.categoryRowInactive : ''}`}
                                        >
                                            {editId === cat.categoryId ? (
                                                <div className={styles.editBlock}>
                                                    <input
                                                        type="text"
                                                        value={editName}
                                                        onChange={e => setEditName(e.target.value)}
                                                        className={styles.inlineInput}
                                                        autoFocus
                                                    />
                                                    <input
                                                        type="text"
                                                        value={editDesc}
                                                        onChange={e => setEditDesc(e.target.value)}
                                                        className={styles.inlineInput}
                                                        placeholder="Description (optional)"
                                                    />
                                                    <div className={styles.inlineFormActions}>
                                                        <button
                                                            className={styles.saveBtn}
                                                            onClick={() => handleEditSave(cat)}
                                                            disabled={editLoading}
                                                        >
                                                            {editLoading ? 'Saving...' : 'Save'}
                                                        </button>
                                                        <button className={styles.cancelBtn} onClick={() => setEditId(null)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.catInfo}>
                                                        <div className={styles.catDot} />
                                                        <div>
                                                            <span className={styles.catName}>{cat.categoryName}</span>
                                                            {cat.categoryDescription && (
                                                                <span className={styles.catDesc}>{cat.categoryDescription}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={styles.catActions}>
                                                        {/* Toggle */}
                                                        <button
                                                            className={`${styles.toggleBtn} ${cat.isActive ? styles.toggleActive : styles.toggleInactive}`}
                                                            onClick={() => handleToggleActive(cat)}
                                                            title={cat.isActive ? 'Deactivate' : 'Activate'}
                                                        >
                                                            {cat.isActive ? 'Active' : 'Inactive'}
                                                        </button>
                                                        <button
                                                            className={styles.iconBtn}
                                                            title="Edit"
                                                            onClick={() => {
                                                                setEditId(cat.categoryId);
                                                                setEditName(cat.categoryName);
                                                                setEditDesc(cat.categoryDescription || '');
                                                            }}
                                                        >
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                                            title="Delete"
                                                            onClick={() => handleDeleteCategory(cat)}
                                                        >
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6" />
                                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                                <path d="M10 11v6M14 11v6" />
                                                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* RIGHT: Standard Categories (Import) */}
                            <div className={styles.panel}>
                                <div className={styles.panelHeader}>
                                    <div className={styles.panelTitleGroup}>
                                        <span className={styles.panelTitle}>Standard Categories</span>
                                        <span className={styles.panelCount}>{notImportedStandard.length}</span>
                                    </div>
                                    <span className={styles.panelHint}>Click to import</span>
                                </div>

                                <p className={styles.standardNote}>
                                    Lokaci-curated categories for salon businesses. Import any to your list.
                                </p>

                                <div className={styles.categoryList}>
                                    {notImportedStandard.length === 0 && (
                                        <div className={styles.emptyState}>
                                            All standard categories have been imported.
                                        </div>
                                    )}
                                    {notImportedStandard.map((cat, idx) => (
                                        <div key={idx} className={styles.standardRow}>
                                            <div className={styles.catInfo}>
                                                <div className={`${styles.catDot} ${styles.catDotGray}`} />
                                                <div>
                                                    <span className={styles.catName}>{cat.categoryName}</span>
                                                    {cat.categoryDescription && (
                                                        <span className={styles.catDesc}>{cat.categoryDescription}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                className={styles.importBtn}
                                                onClick={() => handleImportStandard(cat)}
                                            >
                                                Import
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ExpenseSetting;